import type { StorageStrategy } from '@components/room/streaming-debugger/stores/logger/storage-strategy/index';
import type { LogEntry } from '@components/room/streaming-debugger/stores/logger/types';

const DB_NAME = 'LoggerDB';
const STORE_NAME = 'logs';

export class IndexedDBStrategy implements StorageStrategy {
    private db: IDBDatabase | null = null;

    private promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
        return new Promise((resolve, reject) => {
            // eslint-disable-next-line no-param-reassign
            request.onerror = () => reject(request.error);
            // eslint-disable-next-line no-param-reassign
            request.onsuccess = () => resolve(request.result);
        });
    }

    async init(): Promise<void> {
        try {
            this.db = await this.openDB();
            await this.clear();
        } catch (error) {
            console.error('Failed to initialize IndexedDB:', error);
            throw error;
        }
    }

    private openDB(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, 1);
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                db.createObjectStore(STORE_NAME, { autoIncrement: true });
            };
        });
    }

    async add(log: LogEntry): Promise<void> {
        if (!this.db) throw new Error('Database not initialized');
        const transaction = this.db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        await this.promisifyRequest(store.add(log));
    }

    async getAll(): Promise<LogEntry[]> {
        if (!this.db) throw new Error('Database not initialized');
        const transaction = this.db.transaction(STORE_NAME, 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const result = await this.promisifyRequest(store.getAll());

        return result as LogEntry[];
    }

    async clear(): Promise<void> {
        if (!this.db) throw new Error('Database not initialized');
        const transaction = this.db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        await this.promisifyRequest(store.clear());
    }
}